import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import CollabPicker from "@/components/CollabPicker";
import { toast } from "sonner";
import { fireTutorialTrigger } from "@/lib/tutorialTriggers";
import { useTutorial } from "@/components/tutorial/TutorialProvider";
import { SUBMIT_MUSIC_GENRES as GENRES } from "@/lib/musicGenres";


const SubmitMusicPage = () => {
  const [user, setUser] = useState(null);
  const { startSection } = useTutorial();
  const [formData, setFormData] = useState({
    track_title: "",
    artist_name: "",
    genre: "",
    description: ""
  });
  const [files, setFiles] = useState({
    music_file: null,
    cover_art: null,
    epk_file: null,
    press_photos: []
  });
  const [collaborators, setCollaborators] = useState([]); // Phase 3.2 collaborator tags
  const [coverPreview, setCoverPreview] = useState(null); // Phase 3.2 instant preview
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Phase 21.4 Batch 11 — Auto-enter into a contest on upload.
  // Optional dropdown lists currently OPEN contests that accept tracks.
  const [openContests, setOpenContests] = useState([]);
  const [selectedContestId, setSelectedContestId] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get(`/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Phase 21.4 Batch 11 — Load open track-accepting contests.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/contests', { params: { status: 'open' } });
        if (cancelled) return;
        const list = (r.data?.contests || []).filter(
          (c) => (c.entry_type || 'track') === 'track',
        );
        setOpenContests(list);
      } catch { /* feature flag off or no open contests — silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [fieldName]: file }));
      // Phase 3.2: instant preview for cover art so the user sees it before submit
      if (fieldName === 'cover_art' && file.type?.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setCoverPreview(ev.target?.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleMultipleFiles = (e) => {
    const fileList = Array.from(e.target.files);
    setFiles(prev => ({ ...prev, press_photos: fileList }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.track_title.trim()) {
      toast.error("Track title is required");
      return;
    }
    if (!formData.artist_name.trim()) {
      toast.error("Artist name is required");
      return;
    }
    if (!formData.genre) {
      toast.error("Please select a genre");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Description is required");
      return;
    }
    if (!files.music_file) {
      toast.error("Music file is required");
      return;
    }
    if (!files.cover_art) {
      toast.error("Cover art is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Add required text fields
      formDataToSend.append("track_title", formData.track_title);
      formDataToSend.append("artist_name", formData.artist_name);
      formDataToSend.append("genre", formData.genre);
      formDataToSend.append("description", formData.description);

      // Add required files
      formDataToSend.append("music_file", files.music_file);
      formDataToSend.append("cover_art", files.cover_art);

      // Add optional files
      if (files.epk_file) {
        formDataToSend.append("epk_file", files.epk_file);
      }

      if (files.press_photos.length > 0) {
        files.press_photos.forEach((photo) => {
          formDataToSend.append("press_photos", photo);
        });
      }

      if (collaborators.length > 0) {
        formDataToSend.append("featured_artists_json", JSON.stringify(collaborators));
      }

      const response = await api.post(`/submissions`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        }
      });

      toast.success("Submission successful!", {
        description: "Your music has been submitted for review."
      });
      // Phase 21.4 Batch 11 — Auto-enter into the selected contest.
      const newSubmissionId = response?.data?.submission_id
        || response?.data?.submission?.submission_id;
      if (selectedContestId && newSubmissionId) {
        try {
          await api.post(`/contests/${selectedContestId}/entries`, {
            entry_type: "track",
            ref_id: newSubmissionId,
          });
          toast.success("🏆 Auto-entered into the selected contest!", {
            description: "Track ID: " + newSubmissionId,
          });
        } catch (contestErr) {
          // Non-fatal — surface but don't block the upload success.
          toast.error(
            contestErr?.response?.data?.detail
            || "Contest entry failed — you can enter manually from Contests.",
          );
        }
      }
      // Phase 15.2 #6 — contextual nudge: first-time uploader gets a
      // smart "share to kick momentum" tip + jump-link into the
      // upload_track tutorial's share step.
      fireTutorialTrigger("first_upload", { openTutorial: startSection });
      navigate("/dashboard");
    } catch (error) {
      console.error('Submission error:', error);
      toast.error("Submission failed", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
      {/* Header */}
      <div className="border-b border-gray-800" style={{ background: '#0f0f11' }}>
        <div className="container-custom">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-10 w-auto object-contain" />
            </Link>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="text-white border-gray-700"
              data-testid="back-to-dashboard"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container-custom py-8">
        <Card className="max-w-2xl mx-auto p-8" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
          <h2 className="text-3xl font-bold text-white mb-2" data-testid="form-title">
            Submit Your Music
          </h2>
          <p className="text-gray-400 mb-2">
            Submissions are FREE during beta. Top 3 artists selected weekly.
          </p>
          <a
            href="/submit-beat"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-pink-300 hover:text-pink-200 mb-6"
            data-testid="submit-music-beat-cta"
          >
            🎹 Uploading a beat instead? Use the Beat upload →
          </a>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="submission-form">
            {/* Track Info */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Track Information</h3>
              
              <div>
                <Label htmlFor="track_title" className="text-gray-300">Track Title *</Label>
                <Input
                  id="track_title"
                  data-testid="input-track-title"
                  value={formData.track_title}
                  onChange={(e) => setFormData({ ...formData, track_title: e.target.value })}
                  placeholder="Enter your track title"
                  className="form-input mt-1"
                />
              </div>

              <div>
                <Label htmlFor="artist_name" className="text-gray-300">Artist Name *</Label>
                <Input
                  id="artist_name"
                  data-testid="input-artist-name"
                  value={formData.artist_name}
                  onChange={(e) => setFormData({ ...formData, artist_name: e.target.value })}
                  placeholder="Your artist/stage name"
                  className="form-input mt-1"
                />
              </div>

              <div>
                <Label htmlFor="genre" className="text-gray-300">Genre *</Label>
                <Select
                  value={formData.genre}
                  onValueChange={(value) => setFormData({ ...formData, genre: value })}
                >
                  <SelectTrigger 
                    id="genre"
                    data-testid="select-genre"
                    className="w-full mt-1 bg-[#1a1a2e] border-gray-700 text-white"
                  >
                    <SelectValue placeholder="Select genre" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-gray-700">
                    {GENRES.map(genre => (
                      <SelectItem 
                        key={genre} 
                        value={genre}
                        className="text-white hover:bg-purple-600/20 focus:bg-purple-600/30 cursor-pointer"
                      >
                        {genre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description" className="text-gray-300">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="input-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="form-textarea mt-1"
                  placeholder="Tell us about this track - inspiration, message, vibe..."
                />
              </div>

              {/* Phase 3.2 / 15.6N: Collaborators */}
              <div>
                <Label className="text-gray-300">Collaborators</Label>
                <p className="text-xs text-gray-500 mb-2">Tag featured artists, producers, beat credits, or off-platform contributors. They'll be notified when this track posts.</p>
                <CollabPicker
                  value={collaborators}
                  onChange={setCollaborators}
                  currentUserId={user?.user_id}
                />
              </div>
            </div>

            {/* File Uploads */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Upload Files</h3>
              
              <div>
                <Label htmlFor="music_file" className="text-gray-300">Music File (MP3/WAV) *</Label>
                <Input
                  id="music_file"
                  type="file"
                  accept=".mp3,.wav"
                  data-testid="input-music-file"
                  onChange={(e) => handleFileChange(e, 'music_file')}
                  className="form-input mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700"
                />
                {files.music_file && (
                  <p className="text-sm text-green-400 mt-1">✓ {files.music_file.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="cover_art" className="text-gray-300">Cover Art (JPG/PNG) *</Label>
                <Input
                  id="cover_art"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  data-testid="input-cover-art"
                  onChange={(e) => handleFileChange(e, 'cover_art')}
                  className="form-input mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700"
                />
                {files.cover_art && (
                  <p className="text-sm text-green-400 mt-1">✓ {files.cover_art.name}</p>
                )}
                {/* Phase 3.2: Instant cover preview */}
                {coverPreview && (
                  <div className="mt-3 inline-block" data-testid="cover-preview">
                    <img src={coverPreview} alt="cover preview" className="w-32 h-32 object-cover rounded-lg border border-purple-500/40" />
                  </div>
                )}
              </div>

              {/* Optional Files */}
              <div className="pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-500 mb-4">Optional files</p>

                {/* Phase 21.4 Batch 11 — Optional: auto-enter this
                    upload into an open contest. Silent when there are
                    no open contests. */}
                {openContests.length > 0 && (
                  <div className="mb-4" data-testid="contest-auto-enter-block">
                    <Label htmlFor="contest_select" className="text-gray-300">
                      🏆 Enter into a contest (optional)
                    </Label>
                    <select
                      id="contest_select"
                      data-testid="input-contest-select"
                      value={selectedContestId}
                      onChange={(e) => setSelectedContestId(e.target.value)}
                      className="form-input mt-1 w-full bg-black/40 border border-gray-700 rounded-md px-3 py-2 text-white"
                    >
                      <option value="">— No contest —</option>
                      {openContests.map((c) => (
                        <option key={c.contest_id} value={c.contest_id}>
                          {c.title || c.slug || c.contest_id}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Your track will be submitted to the selected contest after upload succeeds.
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="epk_file" className="text-gray-300">EPK (PDF)</Label>
                  <Input
                    id="epk_file"
                    type="file"
                    accept=".pdf"
                    data-testid="input-epk"
                    onChange={(e) => handleFileChange(e, 'epk_file')}
                    className="form-input mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700"
                  />
                  {files.epk_file && (
                    <p className="text-sm text-green-400 mt-1">✓ {files.epk_file.name}</p>
                  )}
                </div>

                <div className="mt-4">
                  <Label htmlFor="press_photos" className="text-gray-300">Press Photos</Label>
                  <Input
                    id="press_photos"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    multiple
                    data-testid="input-press-photos"
                    onChange={handleMultipleFiles}
                    className="form-input mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700"
                  />
                  {files.press_photos.length > 0 && (
                    <p className="text-sm text-green-400 mt-1">✓ {files.press_photos.length} files selected</p>
                  )}
                </div>
              </div>
            </div>

            {/* Phase 3.2: Upload progress */}
            {isSubmitting && uploadProgress > 0 && (
              <div className="space-y-2" data-testid="upload-progress">
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Uploading…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all"
              data-testid="submit-button"
            >
              {isSubmitting ? "Submitting..." : "Submit Your Music"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default SubmitMusicPage;
